import { Component, Input, computed, signal, ElementRef, ViewChild, OnInit, OnDestroy } from '@angular/core';

import { cva } from '../utils/class-variance.util';
import { ThemeSection } from '../services/theme.service';
import { TooltipVariant, TooltipPosition, TooltipTrigger } from '../models/tooltip.model';

const tooltipVariants = cva({
  base: [
    'absolute z-9999 px-3 py-2 text-sm font-medium rounded-xl',
    'backdrop-blur-xl border shadow-2xl',
    'transition-all duration-300 ease-out transform-gpu',
    'pointer-events-none select-none',
    // Glass morphism base
    'before:content-[""] before:absolute before:inset-0',
    'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
    'before:opacity-80 before:rounded-xl before:pointer-events-none',
    'before:transition-opacity before:duration-300',
    // Arrow base
    'after:content-[""] after:absolute after:w-2 after:h-2',
    'after:bg-inherit after:border-inherit after:rotate-45',
  ].join(' '),
  variants: {
    variant: {
      default: [
        'bg-gray-900/90 text-white border-gray-700/50',
        'dark:bg-white/90 dark:text-gray-900 dark:border-white/50',
      ].join(' '),
      dark: ['bg-gray-900/95 text-white border-gray-700/60', 'shadow-black/30'].join(' '),
      light: ['bg-white/95 text-gray-900 border-gray-200/60', 'shadow-gray-500/20'].join(' '),
      primary: ['bg-primary-600/95 text-white border-primary-500/60', 'shadow-primary-500/30'].join(' '),
      success: ['bg-cyan-600/95 text-white border-cyan-500/60', 'shadow-cyan-500/30'].join(' '),
      warning: ['bg-yellow-600/95 text-white border-yellow-500/60', 'shadow-yellow-500/30'].join(' '),
      error: ['bg-red-600/95 text-white border-red-500/60', 'shadow-red-500/30'].join(' '),
    },
    position: {
      top: 'bottom-full left-1/2 transform -translate-x-1/2 -translate-y-2 after:top-full after:left-1/2 after:-translate-x-1/2 after:-mt-1',
      bottom:
        'top-full left-1/2 transform -translate-x-1/2 translate-y-2 after:bottom-full after:left-1/2 after:-translate-x-1/2 after:-mb-1',
      left: 'right-full top-1/2 transform -translate-y-1/2 -translate-x-2 after:left-full after:top-1/2 after:-translate-y-1/2 after:-ml-1',
      right:
        'left-full top-1/2 transform -translate-y-1/2 translate-x-2 after:right-full after:top-1/2 after:-translate-y-1/2 after:-mr-1',
      'top-start': 'bottom-full left-0 transform -translate-y-2 after:top-full after:left-4 after:-mt-1',
      'top-end': 'bottom-full right-0 transform -translate-y-2 after:top-full after:right-4 after:-mt-1',
      'bottom-start': 'top-full left-0 transform translate-y-2 after:bottom-full after:left-4 after:-mb-1',
      'bottom-end': 'top-full right-0 transform translate-y-2 after:bottom-full after:right-4 after:-mb-1',
    },
    visible: {
      true: 'opacity-100 scale-100',
      false: 'opacity-0 scale-95 pointer-events-none',
    },
  },
  defaultVariants: {
    variant: 'default',
    position: 'top',
    visible: 'false',
  },
});

@Component({
  selector: 'ui-tooltip-glass',
  standalone: true,
  imports: [],
  templateUrl: './tooltip-glass.component.html',
  host: {
    class: 'relative inline-block',
  },
})
export class TooltipGlassComponent implements OnInit, OnDestroy {
  @ViewChild('triggerRef') triggerRef!: ElementRef;
  @ViewChild('tooltipRef') tooltipRef!: ElementRef;

  @Input() content: string = '';
  @Input() variant: TooltipVariant = 'default';
  @Input() position: TooltipPosition = 'top';
  @Input() trigger: TooltipTrigger = 'hover';
  @Input() delay: number = 0;
  @Input() hideDelay: number = 0;
  @Input() disabled: boolean = false;
  @Input() maxWidth: string = '200px';
  @Input() themeSection: ThemeSection = 'global';

  public isVisible = signal(false);
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private boundHandleEscapeKey = this.handleEscapeKey.bind(this);

  public computedTooltipClasses = computed(() => {
    return tooltipVariants({
      variant: this.variant,
      position: this.position,
      visible: this.isVisible() ? 'true' : 'false',
    });
  });

  public tooltipStyle = computed(() => {
    return {
      'max-width': this.maxWidth,
      'z-index': '9999',
    };
  });

  ngOnInit(): void {
    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', this.boundHandleEscapeKey);
    }
  }

  ngOnDestroy(): void {
    this.clearTimers();
    if (typeof document !== 'undefined') {
      document.removeEventListener('keydown', this.boundHandleEscapeKey);
    }
  }

  show(): void {
    if (this.disabled || !this.content) return;

    this.clearTimers();

    if (this.delay > 0) {
      this.showTimer = setTimeout(() => {
        this.isVisible.set(true);
      }, this.delay);
    } else {
      this.isVisible.set(true);
    }
  }

  hide(): void {
    this.clearTimers();

    if (this.hideDelay > 0) {
      this.hideTimer = setTimeout(() => {
        this.isVisible.set(false);
      }, this.hideDelay);
    } else {
      this.isVisible.set(false);
    }
  }

  toggle(): void {
    if (this.isVisible()) {
      this.hide();
    } else {
      this.show();
    }
  }

  private clearTimers(): void {
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  public handleEscapeKey(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.isVisible()) {
      this.hide();
    }
  }
}

/**
 * Usage Examples:
 *
 * Basic tooltip:
 * <ui-tooltip-glass content="This is a helpful tooltip">
 *   <button>Hover me</button>
 * </ui-tooltip-glass>
 *
 * Different variants:
 * <ui-tooltip-glass content="Success message" variant="success">
 *   <ui-icon name="check"></ui-icon>
 * </ui-tooltip-glass>
 *
 * <ui-tooltip-glass content="Error occurred" variant="error">
 *   <ui-icon name="alert-circle"></ui-icon>
 * </ui-tooltip-glass>
 *
 * Different positions:
 * <ui-tooltip-glass content="Top tooltip" position="top">
 *   <button>Top</button>
 * </ui-tooltip-glass>
 *
 * <ui-tooltip-glass content="Bottom tooltip" position="bottom">
 *   <button>Bottom</button>
 * </ui-tooltip-glass>
 *
 * Click trigger:
 * <ui-tooltip-glass content="Click tooltip" trigger="click">
 *   <button>Click me</button>
 * </ui-tooltip-glass>
 *
 * With delays:
 * <ui-tooltip-glass
 *   content="Delayed tooltip"
 *   [delay]="500"
 *   [hideDelay]="200"
 * >
 *   <button>Delayed</button>
 * </ui-tooltip-glass>
 *
 * E-commerce examples:
 *
 * Product info:
 * <ui-tooltip-glass
 *   content="Free shipping on orders over $50"
 *   variant="primary"
 *   position="bottom"
 * >
 *   <ui-icon name="truck"></ui-icon>
 * </ui-tooltip-glass>
 *
 * Cart item help:
 * <ui-tooltip-glass
 *   content="Remove item from cart"
 *   variant="error"
 *   position="left"
 * >
 *   <button class="text-red-500">
 *     <ui-icon name="trash"></ui-icon>
 *   </button>
 * </ui-tooltip-glass>
 *
 * Stock status:
 * <ui-tooltip-glass
 *   content="Only 3 left in stock!"
 *   variant="warning"
 *   position="top-end"
 * >
 *   <span class="text-orange-500">Low Stock</span>
 * </ui-tooltip-glass>
 *
 * Payment security:
 * <ui-tooltip-glass
 *   content="Your payment is secured with 256-bit SSL encryption"
 *   variant="success"
 *   [maxWidth]="'300px'"
 * >
 *   <ui-icon name="shield-check"></ui-icon>
 * </ui-tooltip-glass>
 */
